"use client";

import { FC, useState } from "react";
import styles from "./styles/ProfileCompletionModal.module.scss";
import { useStores } from "../../stores/useStores";
import ApiClient from "../../backends/ApiClient";
import NotificationManager from "../notifications/NotificationManager";
import { Box, Button, Flex, Text, TextField } from "@radix-ui/themes";
import ImageComponent from "../utilities/ImageComponent";
import ModalBackdrop from "./_ModalBackdrop";
import { ThemeProvider } from "../radix/ThemeProvider";

const ProfileCompletionModal: FC<{
  active: boolean;
}> = ({ active }) => {
  const { userStore, organizationStore } = useStores();
  const logo = organizationStore.currentOrganization.logo;
  const [firstName, setFirstName] = useState(userStore.firstName || "");
  const [lastName, setLastName] = useState(userStore.lastName || "");
  const [saving, setSaving] = useState(false);
  const canSave = firstName.trim() && lastName.trim() && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const client = await ApiClient();
      const user = await (client.PATCH(
        "/organizations/{organization_pk}/users/{id}/",
        {
          params: {
            path: {
              organization_pk: organizationStore.organization.id,
              id: userStore.id,
            },
          },
          body: {
            id: userStore.id,
            fname: firstName.trim(),
            lname: lastName.trim(),
          } as never,
        },
      ) as any);
      userStore.updateUser(user);
      NotificationManager.success("Profile updated successfully.");
    } catch {
      NotificationManager.error("Error updating profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!active) return null;

  return (
    <ThemeProvider>
      <ModalBackdrop
        active={active}
        onMouseDown={() => {}}
        opaqueBackground={true}
        zIndex={1050}
      />
      <div className={styles.overlay} role="dialog">
        <div className={styles.dialog}>
          <header className={styles.header}>
            <div className={styles.headerContent}>
              {logo ? (
                <ImageComponent
                  src={logo}
                  aria-label={`${organizationStore.currentOrganization.title} Logo`}
                  className={styles.logo}
                />
              ) : (
                <Text size="5" weight="bold" as="p">
                  {organizationStore.currentOrganization.title}
                </Text>
              )}
              <Text size="3" as="p" color="gray">
                Complete Your Profile
              </Text>
            </div>
          </header>
          <Box className={styles.body}>
            <Flex direction="column" gap="3">
              <Box>
                <Text as="label" size="2" weight="medium" mb="1">
                  First Name
                </Text>
                <TextField.Root
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  size="3"
                  autoFocus
                />
              </Box>
              <Box>
                <Text as="label" size="2" weight="medium" mb="1">
                  Last Name
                </Text>
                <TextField.Root
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  size="3"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                  }}
                />
              </Box>
            </Flex>
            <Flex gap="3" mt="5" justify="end">
              <Button size="3" onClick={handleSave} disabled={!canSave}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </Flex>
          </Box>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default ProfileCompletionModal;
